import {
  AfterViewInit,
  Component,
  ElementRef,
  inject,
  Input,
  NgZone,
  OnDestroy,
  QueryList,
  ViewChild,
  ViewChildren,
} from '@angular/core';
import { AudioLevelMeter } from './audio-level-meter';

@Component({
  selector: 'app-voice-visualiser',
  templateUrl: './voice-visualiser.component.html',
  styleUrls: ['./voice-visualiser.component.scss'],
  standalone: false,
})
export class VoiceVisualiserComponent implements AfterViewInit, OnDestroy {
  private readonly zone = inject(NgZone);

  @Input() meter: AudioLevelMeter | undefined;
  @Input() active = false;

  @ViewChild('strip', { static: true }) private strip: ElementRef<HTMLDivElement>;
  @ViewChildren('bar') private bars: QueryList<ElementRef<HTMLDivElement>>;

  /** Distance between bars in px; must match $bar-width + $bar-spacing in the stylesheet. */
  private readonly PITCH_PX = 6;
  /** Milliseconds a bar takes to slide one pitch; also the sampling cadence. */
  private readonly SAMPLE_INTERVAL_MS = 70;
  /** Bars never fully collapse, so an idle meter still reads as a flat line. */
  private readonly MIN_SCALE = 0.05;
  /** Per-frame rise towards a louder reading; near 1 snaps up almost instantly. */
  private readonly ATTACK = 0.5;
  /** Per-frame fall towards a quieter reading; small values decay gently. */
  private readonly RELEASE = 0.12;
  /** One more bar than fits, so a fresh sample can slide in from the edge. */
  readonly BAR_COUNT = Math.ceil(300 / this.PITCH_PX) + 1;

  readonly barIndices = Array.from({ length: this.BAR_COUNT }, (_, i) => i);

  private readonly levels = new Float32Array(this.BAR_COUNT);
  private frameHandle = 0;
  private lastTimestamp = 0;
  private offset = 0;
  private envelope = 0;

  ngAfterViewInit() {
    this.zone.runOutsideAngular(() => this.tick(0));
  }

  private tick = (timestamp: number) => {
    const elapsed = this.lastTimestamp ? timestamp - this.lastTimestamp : 0;
    this.lastTimestamp = timestamp;

    this.meter?.sample();
    const raw = this.meter?.level() ?? 0;
    const factor = raw > this.envelope ? this.ATTACK : this.RELEASE;
    this.envelope += (raw - this.envelope) * factor;

    this.offset += this.PITCH_PX * (elapsed / this.SAMPLE_INTERVAL_MS);
    let shifted = false;
    while (this.offset >= this.PITCH_PX) {
      this.offset -= this.PITCH_PX;
      this.levels.copyWithin(1, 0, this.BAR_COUNT - 1);
      shifted = true;
    }
    this.levels[0] = this.envelope;

    if (shifted) {
      this.renderAll();
    } else {
      this.renderLeading();
    }
    this.strip.nativeElement.style.transform = `translateX(${-this.offset}px)`;
    this.frameHandle = requestAnimationFrame(this.tick);
  };

  private renderLeading() {
    const bar = this.bars?.first;
    if (bar) {
      bar.nativeElement.style.transform = `scaleY(${this.scaleFor(this.levels[0])})`;
    }
  }

  private renderAll() {
    this.bars?.forEach((bar, i) => {
      bar.nativeElement.style.transform = `scaleY(${this.scaleFor(this.levels[i])})`;
    });
  }

  private scaleFor(level: number) {
    return this.MIN_SCALE + (1 - this.MIN_SCALE) * level;
  }

  ngOnDestroy() {
    cancelAnimationFrame(this.frameHandle);
  }
}
